import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { apiClient } from '../../api'
import { Feature } from '../../core/client/features.gen'
import { useFeature } from '../../core/features/FeaturesProvider'
import { addAttachments, Attachment, AttachmentState, MAX_FILES_PER_MESSAGE } from '../../utils/documentUtils'

/**
 * The files attached to the message being written. Each file uploads on its
 * own as soon as it is added; its chip shows the progress or the failure.
 * Attachments are identified by their `File`.
 */
export function useDocumentUpload() {
  const { t } = useTranslation()
  const [attachments, setAttachments] = useState<Attachment[]>([])
  // Lets the callbacks below read the list without changing identity, so
  // listeners that hold them are not re-attached on every change.
  const latest = useRef(attachments)
  latest.current = attachments
  const imagesEnabled = useFeature(Feature.IMAGE_INSPECTION)

  const set = useCallback((file: File, state: AttachmentState) => {
    setAttachments(prev => prev.map(attachment => (attachment.file === file ? { file, ...state } : attachment)))
  }, [])

  const upload = useCallback(
    async (file: File) => {
      try {
        const { document: asset } = await apiClient.document.uploadDocument(file)
        // The chip was removed while this file uploaded.
        if (!latest.current.some(attachment => attachment.file === file)) {
          apiClient.document.deleteDocument(asset.id).catch(() => undefined)
          return
        }
        set(file, { status: 'done', asset })
      } catch (error) {
        // A rejected file fails the same way again, so it gets no retry. A network
        // error, a server error, a timeout (408) or "too many requests" may pass later.
        const httpStatus = (error as { status?: number }).status
        const rejected = !!httpStatus && httpStatus < 500 && httpStatus !== 408 && httpStatus !== 429
        set(
          file,
          rejected
            ? { status: 'error', error: 'documentRejected', message: (error as Error).message }
            : { status: 'error', error: 'documentUploadFailed' },
        )
      }
    },
    [set],
  )

  const addFiles = useCallback(
    (files: File[]) => {
      const { added, tooMany } = addAttachments(latest.current, files, imagesEnabled)
      if (tooMany) {
        toast.error(String(t('chat.tooManyFiles', { max: MAX_FILES_PER_MESSAGE })))
      }
      setAttachments(prev => [...prev, ...added])
      added.filter(attachment => attachment.status === 'uploading').forEach(attachment => upload(attachment.file))
    },
    [imagesEnabled, t, upload],
  )

  const retry = useCallback(
    (file: File) => {
      set(file, { status: 'uploading' })
      upload(file)
    },
    [set, upload],
  )

  // The chip goes at once. If the delete fails the global toast reports it,
  // and the upload is never sent and expires.
  const remove = useCallback((attachment: Attachment) => {
    setAttachments(prev => prev.filter(item => item.file !== attachment.file))
    if (attachment.status === 'done') {
      apiClient.document.deleteDocument(attachment.asset.id).catch(() => undefined)
    }
  }, [])

  const clear = useCallback(() => {
    setAttachments([])
  }, [])

  return { attachments, addFiles, retry, remove, clear }
}
